// Presentation rules for to-dos shared by the To-dos page, the meeting record page and the
// overview (design system 04-data §1.5 filters, §4 dates; 05-content §6 vocabulary). Pure and
// DOM-free: the URL round-trip and the due-date wording are unit-tested under node.

import type { ProjectRefDto, SortDirection, TodoDto, TodoFellowDto, TodoLinkChangeCounts, TodoLinkDto, TodoSortKey } from '../../../shared/api-types.js';
import {
  FELLOW_ITEM_STATE_LABELS,
  FELLOW_WRITE_CODE_LABELS,
  PROJECT_FILED_BY_LABELS,
  TODO_PRIORITY_RANK,
  TODO_STATUSES,
  isOneOf,
  type FellowItemMatchedBy,
  type FellowItemOffer,
  type FellowItemWant,
  type TodoCompletedVia,
  type TodoPriority,
  type TodoStatus,
} from '../../../shared/enums.js';
import { truncate } from '../../../shared/text.js';
import { queryString } from '../../api.js';
import type { IconName } from '../../ui/Icon.js';
import { EM_DASH, dueState, formatDate, formatDateOnly, formatDue, formatCount, formatList, pluralise, type DueState } from '../../ui/format.js';
import type { SortState } from '../../ui/sort-params.js';

export type TodoStatusFilter = TodoStatus | 'all';

export const TODO_STATUS_FILTERS: readonly TodoStatusFilter[] = [...TODO_STATUSES, 'all'];

export const TODO_STATUS_FILTER_LABELS: Readonly<Record<TodoStatusFilter, string>> = {
  open: 'Open',
  done: 'Completed',
  dismissed: 'Dismissed',
  all: 'Any status',
};

export interface TodoFilters {
  status: TodoStatusFilter;
  meetingId: string | null;
  q: string;
  /** "Changed through a link" (the notice's Review, design §7): only linked to-dos a change through the link reached and the reader has not seen. */
  changed: boolean;
  /**
   * "Not ticked off in Fellow" (owner, 2026-09-30; the Fellow panel's Review): only the reader's own to-dos whose tick or
   * reopen did not get through to Fellow and is worth trying again (`?fellowWrite=failed`).
   */
  fellowFailed: boolean;
  /**
   * "From your Slack day" (owner ruling Q5 of 2026-09-30): only the to-dos the daily Slack sweep made, which belong to no
   * meeting (`?from=slack-day`; the API's `fromSweep=1`). Never together with a meeting: the two exclude each other.
   */
  fromSweep: boolean;
  /**
   * "Slack suggests done" (owner ruling Q8(b)): only the reader's own to-dos under which the daily Slack sweep's "Mark
   * done" offer stands (`?doneOffer=1`, the API's own) — the Assistant's "N to-dos Slack suggests are done" leads here.
   */
  doneOffer: boolean;
  /**
   * The project the list is narrowed to (ADR 0017, design §7.7): a project's id — a bookmarked id of a project since
   * merged away resolves to its survivor on the server, and the page rewrites the address — or `none` for the to-dos
   * filed under no project (`PROJECT_NONE_PARAM_VALUE`, the API's own). Null for every project.
   */
  project: string | null;
}

export const DEFAULT_TODO_FILTERS: Readonly<TodoFilters> = Object.freeze({ status: 'open', meetingId: null, q: '', changed: false, fellowFailed: false, fromSweep: false, doneOffer: false, project: null });

/** The URL and API value of the "No project" filter (`?project=none`). */
export const PROJECT_NONE_PARAM_VALUE = 'none';

/** The URL value of the "Not ticked off in Fellow" filter (the API's own `fellowWrite=failed`). */
export const FELLOW_FAILED_PARAM_VALUE = 'failed';

/** The URL value of the "From your Slack day" filter (`?from=slack-day`), and the Meeting select's option for it. */
export const SLACK_DAY_PARAM_VALUE = 'slack-day';

/** What the To-dos page calls the daily sweep's meeting-less to-dos, where others name their meeting. */
export const SLACK_DAY_LABEL = 'Your Slack day';
export const SLACK_DAY_FILTER_LABEL = 'From your Slack day';

/** The "Slack suggests done" filter's chip. */
export const DONE_OFFER_FILTER_LABEL = 'Slack suggests done';

/** Query-string keys, optionally prefixed so an embedded table does not collide with its page. */
export function todoFilterParamNames(prefix = ''): { status: string; meeting: string; q: string; changed: string; fellowWrite: string; from: string; doneOffer: string; project: string } {
  return { status: `${prefix}status`, meeting: `${prefix}meeting`, q: `${prefix}q`, changed: `${prefix}changed`, fellowWrite: `${prefix}fellowWrite`, from: `${prefix}from`, doneOffer: `${prefix}doneOffer`, project: `${prefix}project` };
}

/**
 * The filters a "Clear all" (or the filtered-empty state's clear) leaves: the default open view, keeping the meeting
 * only where the page fixes it.
 */
export function clearedTodoFilters(filters: TodoFilters, options: { keepMeeting: boolean }): TodoFilters {
  return { ...DEFAULT_TODO_FILTERS, meetingId: options.keepMeeting ? filters.meetingId : null };
}

/** Reads the filters from the URL; a damaged value degrades to the default rather than an error. */
export function parseTodoFilters(params: URLSearchParams, prefix = ''): TodoFilters {
  const names = todoFilterParamNames(prefix);
  const rawStatus = params.get(names.status);
  const meeting = params.get(names.meeting);
  const meetingId = meeting && meeting.trim() !== '' ? meeting.trim() : null;
  const project = params.get(names.project);
  return {
    status: isOneOf(TODO_STATUS_FILTERS, rawStatus) ? rawStatus : DEFAULT_TODO_FILTERS.status,
    meetingId,
    q: (params.get(names.q) ?? '').trim(),
    changed: params.get(names.changed) === '1',
    fellowFailed: params.get(names.fellowWrite) === FELLOW_FAILED_PARAM_VALUE,
    // A link naming a meeting and the Slack day keeps the meeting: the two exclude each other, and the meeting is the narrower.
    fromSweep: meetingId === null && params.get(names.from) === SLACK_DAY_PARAM_VALUE,
    doneOffer: params.get(names.doneOffer) === '1',
    project: project && project.trim() !== '' ? project.trim() : null,
  };
}

/** Writes the filters back; defaults are written as an absence so a pristine URL stays clean. */
export function writeTodoFilters(params: URLSearchParams, filters: TodoFilters, prefix = ''): URLSearchParams {
  const names = todoFilterParamNames(prefix);
  const next = new URLSearchParams(params);
  if (filters.status === DEFAULT_TODO_FILTERS.status) next.delete(names.status);
  else next.set(names.status, filters.status);
  if (filters.meetingId) next.set(names.meeting, filters.meetingId);
  else next.delete(names.meeting);
  if (filters.q.trim() !== '') next.set(names.q, filters.q.trim());
  else next.delete(names.q);
  if (filters.changed) next.set(names.changed, '1');
  else next.delete(names.changed);
  if (filters.fellowFailed) next.set(names.fellowWrite, FELLOW_FAILED_PARAM_VALUE);
  else next.delete(names.fellowWrite);
  if (filters.fromSweep && !filters.meetingId) next.set(names.from, SLACK_DAY_PARAM_VALUE);
  else next.delete(names.from);
  if (filters.doneOffer) next.set(names.doneOffer, '1');
  else next.delete(names.doneOffer);
  if (filters.project) next.set(names.project, filters.project);
  else next.delete(names.project);
  return next;
}

/** How many filters narrow the list beyond the default open view. */
export function activeTodoFilterCount(filters: TodoFilters, options: { ignoreMeeting?: boolean; ignoreProject?: boolean } = {}): number {
  let count = 0;
  if (filters.status !== DEFAULT_TODO_FILTERS.status) count += 1;
  if (filters.meetingId && !options.ignoreMeeting) count += 1;
  if (filters.q.trim() !== '') count += 1;
  if (filters.changed) count += 1;
  if (filters.fellowFailed) count += 1;
  if (filters.fromSweep && !filters.meetingId && !options.ignoreMeeting) count += 1;
  if (filters.doneOffer) count += 1;
  if (filters.project && !options.ignoreProject) count += 1;
  return count;
}

/**
 * The count on the phone's single Filters button (design system 03-components §10, Mobile: the button shows
 * the active count): every filter but the search, which keeps its own field beside the button. Why
 * "Changed through a link" counts although the sheet has no field for it: it narrows the list like any
 * other, and a count that left it out read "Filters (1)" beside two removable chips.
 */
export function filterButtonCount(filters: TodoFilters, options: { ignoreMeeting?: boolean; ignoreProject?: boolean } = {}): number {
  return activeTodoFilterCount({ ...filters, q: '' }, options);
}

/** Sort keys the API orders by itself; anything else is sorted on the loaded rows. */
export const TODO_SERVER_SORT_KEYS: readonly TodoSortKey[] = ['due_date', 'priority', 'created_at', 'text', 'meeting', 'project'];

/**
 * The `/api/todos` query for a filter + sort. A fixed meeting (the record page) always wins over a
 * meeting filter from the URL, so a shared link cannot widen the embedded table to other meetings;
 * a fixed project (the project page) wins over a project filter the same way.
 */
export function todoListPath(filters: TodoFilters, sort: SortState, options: { fixedMeetingId?: string | null; fixedProjectId?: string | null } = {}): string {
  const serverSort = isOneOf(TODO_SERVER_SORT_KEYS, sort.key) ? sort : null;
  return `/api/todos${queryString({
    status: filters.status,
    meetingId: options.fixedMeetingId ?? filters.meetingId,
    q: filters.q.trim() || null,
    changed: filters.changed ? '1' : null,
    fellowWrite: filters.fellowFailed ? FELLOW_FAILED_PARAM_VALUE : null,
    // Never beside a meeting (the API refuses both): a fixed or chosen meeting wins.
    fromSweep: filters.fromSweep && !(options.fixedMeetingId ?? filters.meetingId) ? '1' : null,
    doneOffer: filters.doneOffer ? '1' : null,
    project: options.fixedProjectId ?? filters.project,
    sort: serverSort?.key ?? null,
    dir: serverSort ? (serverSort.dir as SortDirection) : null,
  })}`;
}

// --- Projects (ADR 0017; owner rulings of 2026-10-02) ----------------------------------------------
//
// What the to-do and meeting lists say of the project layer: the search control's reach, the project
// filter's chip and what the server's echo of that filter means for a bookmarked address, the words
// of a filing's toast, and the meeting record's row. Pure, so the sentences are pinned under node.

/** The quick search names what it reaches (04-data §1.5): project names only where the floor lets `q` match them. */
export function todoSearchLabel(projectsOn: boolean): string {
  return projectsOn ? 'Search to-do text, responsible person and project' : 'Search to-do text and responsible person';
}

/** A project as a filter option or a chip needs: its id, its name and, when the server sent one, its colour slot. */
export interface ProjectFilterOption {
  id: string;
  name: string;
  slot?: number | null;
}

/** What the server echoes of the project a list was filtered by (TodoListResponse / MeetingListResponse). */
export interface ProjectFilterEcho {
  project: ProjectRefDto | null;
  resolvedFrom: string | null;
}

export type ProjectFilterEchoAction =
  /** The address named a project since merged away: the server answered its survivor's rows, and the address follows. */
  | { kind: 'rewrite'; to: ProjectRefDto; notice: string }
  /** The address named no project of the reader's: the server dropped the filter, and the page says so. */
  | { kind: 'drop'; notice: string };

/** The status line after a bookmarked project id resolved to the project it was merged into (design §7.7). */
export function mergedIntoNotice(survivorName: string): string {
  return `Showing ${survivorName} — the project in your link was merged into it.`;
}

/** The status line after a bookmarked project id named none of the reader's projects. */
export const PROJECT_FILTER_GONE_NOTICE = 'That project no longer exists, so the project filter was dropped.';

/**
 * What a list's answer says about the project filter the address holds, and what the page should do about it (design
 * §2.3, §7.7): rewrite the address to the survivor when the server resolved an absorbed id (`resolvedFrom` names the
 * id asked for, so the answer is certainly this request's), or drop the filter when the id names nothing. Why `known`
 * and `settled` guard the drop: a list keeps its last rows on screen while it reloads, so an answer with no project in
 * it may belong to the filters before this one — a project the reader just chose from the select is one of their own
 * live projects (`known`), and an answer still on its way is never read as "nothing found". Null: nothing to do, the
 * chip is named from the echo or the options.
 */
export function projectFilterEchoAction(requested: string | null, echo: ProjectFilterEcho | null, known: readonly string[] | null, settled: boolean): ProjectFilterEchoAction | null {
  if (!requested || requested === PROJECT_NONE_PARAM_VALUE || !echo) return null;
  if (echo.resolvedFrom === requested && echo.project) return { kind: 'rewrite', to: echo.project, notice: mergedIntoNotice(echo.project.name) };
  if (echo.project?.id === requested) return null;
  if (echo.project === null && echo.resolvedFrom === null && settled && known !== null && !known.includes(requested)) return { kind: 'drop', notice: PROJECT_FILTER_GONE_NOTICE };
  return null;
}

/**
 * The project the filter chip names: the server's echo first (the live project, so an absorbed id shows its survivor's
 * name before the address is rewritten), else the reader's own option of that id; null when neither knows it yet.
 */
export function projectFilterProject(requested: string | null, echo: ProjectFilterEcho | null, options: readonly ProjectFilterOption[] | null): ProjectFilterOption | null {
  if (!requested || requested === PROJECT_NONE_PARAM_VALUE) return null;
  if (echo?.project && (echo.project.id === requested || echo.resolvedFrom === requested)) return echo.project;
  return options?.find((option) => option.id === requested) ?? null;
}

/**
 * The Project filter select's empty choice: "Any project" once the reader's projects are there, "Loading projects…" only
 * while they are on their way, and, once that load has failed, that they are not loaded — never a loading word left
 * standing over a failure (app-baseline 05-client §5.4; Phase 1c finding D18).
 */
export function projectFilterPlaceholder(options: readonly ProjectFilterOption[] | null, failed: boolean): string {
  if (options !== null) return 'Any project';
  return failed ? 'Projects not loaded' : 'Loading projects…';
}

/** Beside the Project filter whose projects failed to load: what failed and that the filter waits on it (05-client §5.5). */
export const PROJECT_FILTER_FAILED_HINT = 'We couldn’t load your projects, so this filter is unavailable.';

/** The chip's words for the "No project" filter, and the select's option for it. */
export const PROJECT_NONE_FILTER_LABEL = 'No project';
export const PROJECT_NONE_CHIP_LABEL = 'Project: none';
/** The chip while neither the answer nor the options name the project yet. */
export const PROJECT_UNKNOWN_CHIP_LABEL = 'Project: selected project';

/**
 * The toast after a filing (design §7.4): "Filed under Alpine Logistics and Hiring", or that nothing holds it now — the
 * names a list the locale joins (05-content §8; the record's "projects' name join", Phase 1c D30), never ", " by hand.
 */
export function filedUnderText(projectNames: readonly string[]): string {
  return projectNames.length === 0 ? 'Filed under no project' : `Filed under ${formatList(projectNames)}`;
}

/**
 * Who filed a meeting's projects, as the record's row says beside the chips (design §7.7; one vocabulary with the
 * audit labels, `PROJECT_FILED_BY_LABELS`): Calis, the person, or both when the set is mixed; null with no project.
 */
export function projectsFiledByNote(projects: ReadonlyArray<Pick<ProjectRefDto, 'setBy'>>): string | null {
  if (projects.length === 0) return null;
  const ai = projects.some((project) => project.setBy === 'ai');
  const person = projects.some((project) => project.setBy === 'person');
  if (ai && person) return `${PROJECT_FILED_BY_LABELS.ai}, and by you`;
  if (person) return PROJECT_FILED_BY_LABELS.person;
  return PROJECT_FILED_BY_LABELS.ai;
}

/** What the Regenerate confirmation adds under the projects floor (design §7.7): the sorting runs again and a person's choice stands. */
export const REGENERATE_PROJECTS_SENTENCE = 'Calis sorts the meeting and its to-dos into your projects again, keeping any you chose.';

// --- The daily Slack sweep's "Mark done" offer (owner ruling Q8(b)) ----------------------------------

/**
 * The words of the line under a to-do the daily sweep suggests is done — null when there is nothing to show: no offer
 * standing, or the to-do not open (the server sends none then; a row ticked on the page meanwhile drops it at once).
 */
export function doneOfferLine(todo: Pick<TodoDto, 'doneOffer' | 'status'>): string | null {
  if (!todo.doneOffer || todo.status !== 'open') return null;
  return `Slack suggests this is done — “${todo.doneOffer.reason}”`;
}

/** The route Mark done or Not done calls. */
export function doneOfferPath(todoId: string, action: 'accept' | 'dismiss'): string {
  return `/api/todos/${encodeURIComponent(todoId)}/done-offer/${action}`;
}

/** What Not done says: the to-do stays as it is, and the same evidence is never offered again (design §4). */
export const DONE_OFFER_DISMISSED = 'Kept open. Calis won’t suggest it again unless Slack shows something new.';

/** The Assistant's count line, leading to the "Slack suggests done" view. */
export function doneOffersCountLine(count: number): string {
  return pluralise(count, { one: '# to-do Slack suggests is done', other: '# to-dos Slack suggests are done' });
}

/** The To-dos page filtered to the to-dos under which an offer stands. */
export const DONE_OFFER_LIST_PATH = '/todos?doneOffer=1';

/**
 * What a list says in place of the meeting of a to-do that has none: the daily Slack sweep's own (owner ruling Q5),
 * the person's own, or — for one Calis made from a meeting since removed — that meeting's absence.
 */
export function todoWithoutMeetingLabel(todo: Pick<TodoDto, 'source' | 'fromSweep'>): string {
  if (todo.fromSweep) return SLACK_DAY_FILTER_LABEL;
  return todo.source === 'manual' ? 'Added by you' : 'A meeting that was removed';
}

// --- Priority ----------------------------------------------------------------------------------

export interface PriorityPresentation {
  label: string;
  tone: 'danger' | 'warning' | 'neutral';
  icon: IconName;
  rank: number;
}

/** Priority is never colour alone: each level carries its own glyph (01-foundations §7.3). */
export const TODO_PRIORITY_PRESENTATION: Readonly<Record<TodoPriority, PriorityPresentation>> = {
  high: { label: 'High', tone: 'danger', icon: 'arrow-up', rank: TODO_PRIORITY_RANK.high },
  medium: { label: 'Medium', tone: 'warning', icon: 'minus', rank: TODO_PRIORITY_RANK.medium },
  low: { label: 'Low', tone: 'neutral', icon: 'arrow-down', rank: TODO_PRIORITY_RANK.low },
};

// --- Due dates ---------------------------------------------------------------------------------

export interface DuePresentation {
  /** The relative reading: "Overdue by 3 days", "Due today", "Due 18 Oct 2026"; the em dash for none. */
  text: string;
  /** The absolute calendar date for the title and accessible name; "No due date" for none. */
  absolute: string;
  state: DueState | null;
  /** Overdue is marked by tone AND glyph AND words, never colour alone. */
  tone: 'danger' | 'warning' | 'neutral' | 'muted';
  icon: IconName | null;
}

export function duePresentation(dueDate: string | null, today: string, status: TodoStatus): DuePresentation {
  if (!dueDate) return { text: EM_DASH, absolute: 'No due date', state: null, tone: 'muted', icon: null };
  const absolute = formatDateOnly(dueDate);
  if (absolute === EM_DASH) return { text: EM_DASH, absolute: 'No due date', state: null, tone: 'muted', icon: null };
  // Why: a finished to-do is not overdue — its date is history, so it reads as a plain date.
  if (status !== 'open') return { text: absolute, absolute, state: null, tone: 'muted', icon: null };
  const state = dueState(dueDate, today);
  const text = formatDue(dueDate, today);
  switch (state) {
    case 'overdue':
      return { text, absolute, state, tone: 'danger', icon: 'alert-triangle' };
    case 'today':
      return { text, absolute, state, tone: 'warning', icon: 'clock' };
    case 'soon':
      return { text, absolute, state, tone: 'neutral', icon: null };
    case 'later':
    default:
      return { text, absolute, state, tone: 'muted', icon: null };
  }
}

// --- Rows --------------------------------------------------------------------------------------

/** Who is responsible, in the reader's words: "You" when it is theirs and nobody else is named. */
export function responsibleLabel(todo: Pick<TodoDto, 'responsible' | 'assignedToMe'>): string {
  const named = todo.responsible?.trim();
  if (named) return named;
  return todo.assignedToMe ? 'You' : EM_DASH;
}

/**
 * The transcript segment a to-do's quote came from: the last segment starting at or before the
 * offset (a quote is attributed to the moment it began). Null when the offset falls before the
 * first timed segment or the transcript carries no timings.
 */
export function segmentIndexForOffset(segments: ReadonlyArray<{ index: number; start: number | null }>, offsetSeconds: number | null): number | null {
  if (offsetSeconds === null || !Number.isFinite(offsetSeconds)) return null;
  let found: number | null = null;
  for (const segment of segments) {
    if (segment.start === null) continue;
    if (segment.start <= offsetSeconds) found = segment.index;
    else break;
  }
  return found;
}

/**
 * Applies an optimistic status change to one row in a loaded list without touching the others. The
 * change is the person's own, so a done is theirs (`completedVia: 'person'`), as the server records it.
 */
export function withTodoStatus(items: readonly TodoDto[], id: string, status: TodoStatus, now: string): TodoDto[] {
  return items.map((item) =>
    item.id === id ? { ...item, status, updatedAt: now, completedAt: status === 'done' ? now : null, completedVia: status === 'done' ? 'person' : null } : item,
  );
}

// --- Linked to-dos (ADR 0014; ruling Q8(a)) --------------------------------------------------------
//
// What a person is told about their own linked to-do: who it is linked with — by name only when the
// colleague is on the person's own invite list, otherwise "a colleague" — who last ticked it off or
// reopened it and when, and the notice's counts. Never a colleague's words, dates or address: the DTO
// carries none, and these helpers only phrase what it carries.

/** The colleagues a to-do is linked with, as a phrase: "Tamar Abashidze", "Tamar Abashidze and one other colleague", "2 colleagues". */
export function linkedPeoplePhrase(link: Pick<TodoLinkDto, 'names' | 'others'>): string {
  const named = link.names.slice(0, link.others);
  const unnamed = Math.max(link.others - named.length, 0);
  const parts = [...named];
  if (unnamed > 0) {
    parts.push(named.length === 0
      ? pluralise(unnamed, { one: 'a colleague', other: '# colleagues' })
      : pluralise(unnamed, { one: 'one other colleague', other: '# other colleagues' }));
  }
  if (parts.length === 0) return 'a colleague';
  // The locale joins the names (05-content §8): "A, B and one other colleague".
  return formatList(parts);
}

/** The chip's accessible name: "Linked with Tamar Abashidze". */
export function linkedChipName(link: Pick<TodoLinkDto, 'names' | 'others'>): string {
  return `Linked with ${linkedPeoplePhrase(link)}`;
}

/** The popover's explanation (design §7), in two sentences. */
export function linkedExplanation(link: Pick<TodoLinkDto, 'names' | 'others'>): { who: string; what: string } {
  const one = link.others <= 1;
  return {
    who: `Linked with ${linkedPeoplePhrase(link)}, also invited to this meeting.`,
    what: one
      ? 'Ticking it off here ticks off their copy too, and un-ticking reopens both. Your wording, notes and dates stay your own; they never see them.'
      : 'Ticking it off here ticks off their copies too, and un-ticking reopens them all. Your wording, notes and dates stay your own; they never see them.',
  };
}

/**
 * The reason line under a to-do whose current status came through its link (design §7; ADR 0014, S8): "Ticked off
 * by Tamar Abashidze · 28 Sep 2026", "Reopened by a colleague · …", and — for a done Fellow reported, which is
 * never said to be a colleague's tick — "Completed in Fellow · …" (the wording settled after the fourth review of
 * v2, the design's §7). Null when the row's status is its owner's own doing.
 */
export function linkReasonLine(todo: Pick<TodoDto, 'status' | 'link'>, timeZone?: string): string | null {
  const change = todo.link?.lastChange;
  if (!change) return null;
  const current = (change.action === 'done' && todo.status === 'done') || (change.action === 'reopen' && todo.status === 'open');
  if (!current) return null;
  const date = formatDate(change.at, timeZone ? { timeZone } : undefined);
  if (change.by.source === 'fellow') return `${change.action === 'done' ? 'Completed' : 'Reopened'} in Fellow · ${date}`;
  return `${change.action === 'done' ? 'Ticked off' : 'Reopened'} by ${change.by.name ?? 'a colleague'} · ${date}`;
}

/**
 * The offer's words (ruling Q6(a): "<name> ticked off their copy" with Tick off mine; ADR 0014, S8): the colleague
 * by name only when the server named them — an active colleague on the reader's own copy's invite list whose done
 * is their own tick (ruling Q8(a)); otherwise nobody is said to have ticked anything. A copy Fellow reported done is
 * said to be so, never ticked off by its person (invariant 6: it may be the reader's own completion in Fellow).
 * Null when there is no offer: the reader's copy is not open, or no active colleague's copy is done.
 */
export function colleagueDoneLine(todo: Pick<TodoDto, 'status' | 'link'>): string | null {
  if (todo.status !== 'open' || todo.link?.colleagueDone !== true) return null;
  if (todo.link.colleagueDoneSource === 'fellow') return 'A linked copy was completed in Fellow.';
  const name = todo.link.colleagueDoneName;
  return name ? `${name} ticked off their copy.` : 'A linked copy is ticked off.';
}

/** How many unseen changes the notice reports: colleagues' ticks and un-ticks, and Fellow's completions and reopens. */
export function linkChangeTotal(counts: TodoLinkChangeCounts | null | undefined): number {
  return counts ? counts.ticked + counts.reopened + counts.completedInFellow + counts.reopenedInFellow : 0;
}

/** What Fellow reported, as the notice words it: "1 marked done", "2 reopened", "1 marked done and 1 reopened" — the locale joining them (05-content §8). */
function fellowChangesText(counts: TodoLinkChangeCounts): string {
  return formatList([
    counts.completedInFellow > 0 ? `${formatCount(counts.completedInFellow)} marked done` : null,
    counts.reopenedInFellow > 0 ? `${formatCount(counts.reopenedInFellow)} reopened` : null,
  ].filter((part): part is string => part !== null));
}

/**
 * The notice above the table (design §7, F6): "Colleagues ticked off 2 of your linked to-dos.", and — for a change
 * Fellow reported, which may be the reader's own in Fellow — "One of your linked to-dos was completed in Fellow." or
 * "… was reopened in Fellow." (never a colleague's, ADR 0014 invariant 6; a reopen made in Fellow reaches a colleague's
 * copy through S4-F, ADR 0015 Stage 3). Null when there is nothing unseen.
 */
export function linkNoticeText(counts: TodoLinkChangeCounts | null | undefined, options: { inMeeting?: boolean } = {}): string | null {
  const total = linkChangeTotal(counts);
  if (!counts || total === 0) return null;
  // Why every sentence is written out twice, for the page and for one meeting (05-content §8): "in this meeting" is never
  // a phrase spliced into the middle of a sentence — where it goes differs between languages — and each count chooses
  // its sentence whole by the plural rule. The counts and the joined lists are the only values inserted.
  const inMeeting = options.inMeeting === true;
  const count = formatCount(total);
  const fellow = counts.completedInFellow + counts.reopenedInFellow;
  if (fellow > 0) {
    if (counts.ticked + counts.reopened === 0) {
      if (counts.reopenedInFellow === 0) {
        return pluralise(total, inMeeting
          ? { one: 'One of your linked to-dos in this meeting was completed in Fellow.', other: '# of your linked to-dos in this meeting were completed in Fellow.' }
          : { one: 'One of your linked to-dos was completed in Fellow.', other: '# of your linked to-dos were completed in Fellow.' });
      }
      if (counts.completedInFellow === 0) {
        return pluralise(total, inMeeting
          ? { one: 'One of your linked to-dos in this meeting was reopened in Fellow.', other: '# of your linked to-dos in this meeting were reopened in Fellow.' }
          : { one: 'One of your linked to-dos was reopened in Fellow.', other: '# of your linked to-dos were reopened in Fellow.' });
      }
      const changes = fellowChangesText(counts);
      return inMeeting ? `${count} of your linked to-dos in this meeting changed in Fellow: ${changes}.` : `${count} of your linked to-dos changed in Fellow: ${changes}.`;
    }
    const theirs = formatList([counts.ticked > 0 ? `${formatCount(counts.ticked)} ticked off` : null, counts.reopened > 0 ? `${formatCount(counts.reopened)} reopened` : null]
      .filter((part): part is string => part !== null));
    const changes = fellowChangesText(counts);
    return inMeeting
      ? `${count} of your linked to-dos in this meeting changed: ${theirs} by colleagues, ${changes} in Fellow.`
      : `${count} of your linked to-dos changed: ${theirs} by colleagues, ${changes} in Fellow.`;
  }
  if (counts.reopened === 0) {
    return pluralise(total, inMeeting
      ? { one: 'A colleague ticked off one of your linked to-dos in this meeting.', other: 'Colleagues ticked off # of your linked to-dos in this meeting.' }
      : { one: 'A colleague ticked off one of your linked to-dos.', other: 'Colleagues ticked off # of your linked to-dos.' });
  }
  if (counts.ticked === 0) {
    return pluralise(total, inMeeting
      ? { one: 'A colleague reopened one of your linked to-dos in this meeting.', other: 'Colleagues reopened # of your linked to-dos in this meeting.' }
      : { one: 'A colleague reopened one of your linked to-dos.', other: 'Colleagues reopened # of your linked to-dos.' });
  }
  const ticked = formatCount(counts.ticked);
  const reopened = formatCount(counts.reopened);
  return inMeeting
    ? `Colleagues changed ${count} of your linked to-dos in this meeting: ${ticked} ticked off, ${reopened} reopened.`
    : `Colleagues changed ${count} of your linked to-dos: ${ticked} ticked off, ${reopened} reopened.`;
}

/**
 * The overview's request, carrying the browser's time zone (the owner's ruling Q13 of escalation 20261005-200526, "Browser's time zone"): the server cuts "overdue" and "due this week" at the reader's own midnight, as Costs sends its offset.
 */
export function overviewPath(timeZone: string): string {
  return `/api/overview?tz=${encodeURIComponent(timeZone)}`;
}

/** Overview's one line (design §7): "2 to-dos ticked off by colleagues", "1 to-do completed in Fellow", "1 to-do reopened in Fellow". Null when there is nothing unseen. */
export function overviewLinkLine(counts: TodoLinkChangeCounts | null | undefined): string | null {
  const total = linkChangeTotal(counts);
  if (!counts || total === 0) return null;
  // Each line whole, chosen by count (05-content §8): never "a colleague" or "colleagues" substituted into a sentence.
  if (counts.completedInFellow === total) return pluralise(total, { one: '# to-do completed in Fellow', other: '# to-dos completed in Fellow' });
  if (counts.reopenedInFellow === total) return pluralise(total, { one: '# to-do reopened in Fellow', other: '# to-dos reopened in Fellow' });
  if (counts.completedInFellow + counts.reopenedInFellow > 0) return pluralise(total, { one: '# linked to-do changed', other: '# linked to-dos changed' });
  if (counts.reopened === 0) return pluralise(total, { one: '# to-do ticked off by a colleague', other: '# to-dos ticked off by colleagues' });
  if (counts.ticked === 0) return pluralise(total, { one: '# to-do reopened by a colleague', other: '# to-dos reopened by colleagues' });
  return pluralise(total, { one: '# to-do changed by colleagues', other: '# to-dos changed by colleagues' });
}

/**
 * What the toast says after a person's own change of status (03-components §6.1), naming no one (F17), from what
 * the answer to that change says and nothing else: its status, how it was completed, and its link as the person may
 * see it (S8) — never a count of the colleagues' copies the change reached. Why (the sixth review of v2): a tick
 * leaves a dismissed copy as it is (S10), so a toast that said whether the tick closed anyone told the ticker that a
 * colleague had dismissed theirs, which is not the done state (answer 8). So the words hold whatever the colleagues'
 * copies were: a tick with an active colleague closes every linked copy still open (S4) — a dismissed one is not open;
 * an un-tick reopens every linked copy that was ticked off, unless an active colleague's copy is still done after it
 * (an own done made before its person joined stands, S4, and the offer shows it) — then the toast claims nothing. A
 * restore that landed ticked off because its link's state is done (ADR 0014, S10) says so — through the link
 * (`landedVia` 'link'), or as the person's own tick when that state was theirs.
 */
export function statusChangeAnnouncement(change: {
  from: TodoStatus;
  asked: TodoStatus;
  landed: TodoStatus;
  link: Pick<TodoLinkDto, 'colleagueDone'> | null;
  landedVia?: TodoCompletedVia | null;
  /**
   * Whether the answer says a write to Fellow is now on its way (`fellow.pending === 'waiting'`). Why only then (design
   * §9.6, F22): a toast that promised Fellow on every Fellow to-do would be wrong whenever the switch is off, the grant
   * gone, the connection broken or the item not one Calis may write — the row's line holds the durable fact.
   */
  fellowQueued?: boolean;
  /**
   * The change accepted Stage 3's offer to follow Fellow here ("Tick off here", "Reopen here"). Why it is said instead of
   * what goes to Fellow: Fellow already shows it, so the run's live read finds it so and sends nothing — "Ticking it off
   * in Fellow too" would promise what Fellow already did (F22: never overclaim). Only while the answer says nothing is
   * on its way (`fellowQueued` false): when it is — the to-do holds another Fellow item, or Calis owes the item a write
   * all the same, its live read to decide — the row says "Ticking off in Fellow…", and so does the toast (round C review).
   */
  here?: 'tick_here' | 'reopen_here';
}): string {
  if (change.from === 'dismissed' && change.asked !== 'dismissed') {
    // Why first: a tick from dismissed ("Tick off here" on a dismissed row) lands done, and "Restored to open" would say
    // the opposite of the row it sits over.
    if (change.asked === 'done' && change.landed === 'done') return change.here === 'tick_here' ? 'Restored and ticked off here, as in Fellow.' : 'Restored and ticked off.';
    if (change.landed !== 'done' || change.asked === 'done') return 'Restored to open';
    return change.landedVia === 'link' ? 'Restored, already ticked off through a linked copy.' : 'Restored, ticked off as you left it.';
  }
  if (change.asked === 'dismissed') return 'Dismissed';
  if (change.here !== undefined && change.landed === change.asked && change.fellowQueued !== true) {
    if (change.asked === 'done') return `Ticked off here, as in Fellow.${change.link !== null ? ' Linked copies still open are ticked off too.' : ''}`;
    const reopensLinked = change.link !== null && !change.link.colleagueDone;
    return `Reopened here, as in Fellow.${reopensLinked ? ' Linked copies that were ticked off are reopened too.' : ''}`;
  }
  const fellow = change.fellowQueued === true;
  if (change.asked === 'done') {
    if (change.link !== null) return `Completed. Linked copies still open are ticked off too.${fellow ? ' Ticking it off in Fellow too.' : ''}`;
    return fellow ? 'Completed. Ticking it off in Fellow too.' : 'Marked as completed';
  }
  const linked = change.link !== null && !change.link.colleagueDone;
  if (linked) return `Reopened. Linked copies that were ticked off are reopened too.${fellow ? ' Reopening it in Fellow too.' : ''}`;
  return fellow ? 'Reopened. Reopening it in Fellow too.' : 'Reopened';
}

// --- Ticking off in Fellow (owner, 2026-09-30; design §9.6) --------------------------------------------
//
// What a person is told about their own to-do's Fellow item: Fellow's wording and state, whether Calis will tick it
// off there, what is on its way, what did not get through and why, and the one click a look offers. Only what the
// to-do's own `fellow` view carries, and the deployment's switch and the person's grant as /api/me reports them
// (selection only: the routes and the run ask both again).

/**
 * Whether ticking off in Fellow can happen for this reader here: `on`; `off` on this server (FELLOW_WRITE_MODE, the
 * deployment's floor, which binds the superuser too); or `not_allowed` — the switch is on but the reader's account
 * does not include Connect Fellow.
 */
export type FellowTickOffGate = 'on' | 'off' | 'not_allowed';

export function fellowTickOffGate(me: { integrations: { fellowWrite: 'on' | 'off' }; capabilities: { fellowConnect: boolean } }): FellowTickOffGate {
  if (me.integrations.fellowWrite !== 'on') return 'off';
  return me.capabilities.fellowConnect ? 'on' : 'not_allowed';
}

/** After this long, a tick still on its way is said to be waiting rather than in progress (design §9.6). */
export const FELLOW_PENDING_SLOW_MS = 2 * 60_000;

export const FELLOW_GATE_TEXT: Readonly<Record<Exclude<FellowTickOffGate, 'on'>, string>> = {
  off: 'Ticking off in Fellow is off on this server.',
  not_allowed: "Ticking off in Fellow isn't available to you. An administrator can give you 'Connect Fellow'.",
};

/**
 * Whether ticks and reopens made in Fellow come back to Calis for this reader here (Stage 3, "Fellow → Calis"): the
 * deployment's switch (FELLOW_STATE_MODE) on and Connect Fellow theirs. Selection only — the read and the views ask the
 * floor and the grant themselves.
 */
export function fellowStateOn(me: { integrations: { fellowState: 'on' | 'off' }; capabilities: { fellowConnect: boolean } }): boolean {
  return me.integrations.fellowState === 'on' && me.capabilities.fellowConnect;
}

/**
 * What the row's Fellow line offers to do: towards Fellow — try a failed write again, or the one click a look offered
 * (`tick_fellow`, `reopen_fellow`); or, for a change made in Fellow (Stage 3), here — take back the one Calis applied
 * (`undo`), or follow the one it offered (`tick_here`, `reopen_here`). The last three are the person's own status change
 * on this to-do, the checkbox's own request; none writes to Fellow what came from it.
 */
export type FellowLineAction = 'retry' | 'tick_fellow' | 'reopen_fellow' | 'undo' | 'tick_here' | 'reopen_here';

/** The Stage 3 actions: the person's own change of the to-do's status here, never a request about Fellow. */
export type FellowHereAction = Extract<FellowLineAction, 'undo' | 'tick_here' | 'reopen_here'>;

export function isFellowHereAction(action: FellowLineAction): action is FellowHereAction {
  return action === 'undo' || action === 'tick_here' || action === 'reopen_here';
}

/**
 * A change made in Fellow that Calis applied to this to-do (Stage 3), while the to-do still says it. Why the status is
 * checked here too (the server sends it only then): a row ticked or un-ticked a moment ago is drawn with its new status
 * before the list is read again, and "Completed in Fellow" with Undo beside an open checkbox would be false.
 */
export function fellowReported(todo: Pick<TodoDto, 'status' | 'fellow'>): TodoFellowDto['reported'] {
  const reported = todo.fellow?.reported ?? null;
  return reported !== null && todo.status === reported.to ? reported : null;
}

/**
 * "Not the same…" kept apart from this to-do the Fellow item whose change in Fellow had set its status, while the to-do
 * still has that status: its tick (owner ruling C5, "Offer to reopen") — the row offers "Reopen here" and says why it is
 * ticked off — or its reopen (C5's mirror, "Yes, offer it") — the row offers "Tick off here" and says why it is open. Why
 * the status is checked here too (the server sends it only then): a row changed a moment ago is drawn with its new status
 * before the list is read again.
 */
export function fellowKeptApart(todo: Pick<TodoDto, 'status' | 'fellow'>): TodoFellowDto['keptApart'] {
  const apart = todo.fellow?.keptApart ?? null;
  return apart !== null && todo.status === apart.kept ? apart : null;
}

/** The row's line for owner ruling C5: why the to-do is ticked off, beside "Reopen here". */
export const FELLOW_KEPT_APART_LINE = 'Ticked off when an item you have since kept apart was ticked off in Fellow.';

/** The row's line for C5's mirror: why the to-do is open, beside "Tick off here". */
export const FELLOW_KEPT_OPEN_LINE = 'Reopened when an item you have since kept apart was reopened in Fellow.';

/**
 * What that "Tick off here" is, said by the "Not the same…" confirmation before the person confirms (C5's mirror:
 * "clicking is their own tick (reaches colleagues and Fellow)"): the checkbox's own tick, through `changeOwnTodo`. Why it
 * is said from what the to-do keeps once the item is kept apart (round C follow-on review): the tick reaches what any tick
 * of theirs reaches from this to-do — the Fellow items it still holds, and linked copies while it is linked — and in the
 * usual case, the separated item its only one, nothing of theirs in Fellow; promising Fellow there would be false.
 */
export function fellowKeptOpenTick(kept: { fellowItems: boolean; linked: boolean }): string {
  if (kept.fellowItems) return `That is a tick of your own: it reaches ${kept.linked ? 'linked copies and Fellow' : 'Fellow'} as any tick of yours does.`;
  return kept.linked
    ? 'That is a tick of your own: it reaches linked copies as any tick of yours does, and this to-do holds no Fellow item any more.'
    : 'That is a tick of your own, here in Calis: this to-do holds no Fellow item any more.';
}

/**
 * Stage 3's offer to follow here a change made in Fellow ("Tick off here", "Reopen here"), while the to-do's status
 * still makes it one: a tick for an open or dismissed to-do, a reopen for a done one. Null otherwise, and for a look's
 * offers (those go to Fellow).
 */
export function fellowHereOffer(todo: Pick<TodoDto, 'status' | 'fellow'>): Extract<FellowItemOffer, 'tick_here' | 'reopen_here'> | null {
  const offer = todo.fellow?.offer ?? null;
  // Why an offer "Not the same…" left stands only on its own status (C5 and its mirror): it takes back that status, and
  // on a row drawn otherwise a moment ago — dismissed, say — "Completed in Fellow." would say what Fellow was never seen to show.
  const apart = todo.fellow?.keptApart ?? null;
  if (apart !== null && todo.status !== apart.kept) return null;
  if (offer === 'tick_here' && todo.status !== 'done') return offer;
  if (offer === 'reopen_here' && todo.status === 'done') return offer;
  return null;
}

/**
 * The status a Stage 3 action asks for: Undo takes back the change Fellow made (a done is reopened, a reopen ticked off
 * again), Tick off here asks for done, Reopen here for open. Null when the action no longer applies to the row.
 */
export function fellowHereStatus(todo: Pick<TodoDto, 'status' | 'fellow'>, action: FellowHereAction): FellowItemWant | null {
  if (action === 'undo') {
    const reported = fellowReported(todo);
    return reported === null ? null : reported.to === 'done' ? 'open' : 'done';
  }
  return fellowHereOffer(todo) === action ? (action === 'tick_here' ? 'done' : 'open') : null;
}

export interface FellowLinePresentation {
  /** `warning` for a write that failed (glyph, words and colour; 01-foundations §1.5); `muted` for everything else. */
  tone: 'warning' | 'muted';
  text: string;
  action: FellowLineAction | null;
  /** The failure or the pause is the connection's: the line leads to Connections as well. */
  reconnect: boolean;
}

function wantWords(status: TodoStatus): { doing: string; waiting: string; notYet: string } {
  return status === 'done'
    ? { doing: 'Ticking off in Fellow…', waiting: 'Waiting to tick off in Fellow', notYet: 'Not ticked off in Fellow yet.' }
    : { doing: 'Reopening in Fellow…', waiting: 'Waiting to reopen in Fellow', notYet: 'Not reopened in Fellow yet.' };
}

/**
 * The line under a Fellow to-do's row (design §9.6), or null when there is nothing to say — success is quiet. In order:
 * a write that failed (warning, with Try again while the reader may ask for one); a write on its way ("Ticking off in
 * Fellow…", "Waiting to tick off in Fellow" after two minutes; paused while the switch, the grant or the connection
 * stops it); a change made in Fellow that Calis applied here ("Completed in Fellow · 28 Sep 2026", with Undo — Stage 3,
 * design §7.3); the one click a look offered ("Still open in Fellow" — Tick off in Fellow); a change made in Fellow that
 * Calis offers to follow here ("Completed in Fellow." — Tick off here; "Open in Fellow." — Reopen here); a reason Calis could
 * not write it, muted. Why the Stage 3 lines ignore the tick-off gate: their actions change the to-do here and nothing
 * else — the server shows them only to a reader who may see what comes back from Fellow.
 */
export function fellowLine(todo: Pick<TodoDto, 'status' | 'fellow'>, now: Date, gate: FellowTickOffGate = 'on', timeZone?: string): FellowLinePresentation | null {
  const fellow = todo.fellow;
  if (!fellow) return null;
  const words = wantWords(todo.status);
  if (fellow.problem?.kind === 'failure') {
    const reconnect = fellow.problem.code === 'auth_failed';
    const failed = FELLOW_WRITE_CODE_LABELS[fellow.problem.code];
    const text = gate === 'on' ? failed : `${failed} ${FELLOW_GATE_TEXT[gate]}`;
    return { tone: 'warning', text, action: gate === 'on' ? 'retry' : null, reconnect };
  }
  if (fellow.pending === 'waiting') {
    const since = fellow.pendingSince ? Date.parse(fellow.pendingSince) : Number.NaN;
    const slow = Number.isFinite(since) && now.getTime() - since >= FELLOW_PENDING_SLOW_MS;
    return { tone: 'muted', text: slow ? words.waiting : words.doing, action: null, reconnect: false };
  }
  if (fellow.pending === 'paused') {
    // Why the gate decides the words: "paused" is the switch, the grant or the connection, and only the last is the
    // reader's to mend.
    if (gate !== 'on') return { tone: 'muted', text: `${words.notYet} ${FELLOW_GATE_TEXT[gate]}`, action: null, reconnect: false };
    return { tone: 'muted', text: `${words.notYet} It goes once your Fellow connection works again.`, action: null, reconnect: true };
  }
  const reported = fellowReported(todo);
  if (reported) {
    const date = formatDate(reported.at, timeZone ? { timeZone } : undefined);
    return { tone: 'muted', text: reported.to === 'done' ? `Completed in Fellow · ${date}` : `Reopened in Fellow · ${date}`, action: 'undo', reconnect: false };
  }
  if (fellow.offer === 'tick_fellow' || fellow.offer === 'reopen_fellow') {
    const text = fellow.offer === 'tick_fellow' ? 'Still open in Fellow.' : 'Still completed in Fellow.';
    return { tone: 'muted', text: gate === 'on' ? text : `${text} ${FELLOW_GATE_TEXT[gate]}`, action: gate === 'on' ? fellow.offer : null, reconnect: false };
  }
  const here = fellowHereOffer(todo);
  if (here) {
    // C5: the item whose tick in Fellow ticked this to-do off was kept apart from it ("Not the same…"); Fellow may well
    // still show it done, so the line says why the to-do is ticked off, never that Fellow shows it open. Its mirror: the
    // item whose reopen in Fellow reopened it, which Fellow may well still show open — never "Completed in Fellow".
    if (fellowKeptApart(todo)) return { tone: 'muted', text: here === 'reopen_here' ? FELLOW_KEPT_APART_LINE : FELLOW_KEPT_OPEN_LINE, action: here, reconnect: false };
    return { tone: 'muted', text: here === 'tick_here' ? 'Completed in Fellow.' : 'Open in Fellow.', action: here, reconnect: false };
  }
  if (fellow.problem?.kind === 'not_possible') {
    return { tone: 'muted', text: FELLOW_WRITE_CODE_LABELS[fellow.problem.code], action: null, reconnect: false };
  }
  return null;
}

/**
 * Whether a to-do of the reader's is one of their own Calis to-dos that Calis tied a Fellow item to (Stage 2, "Import or
 * tie") rather than a Fellow to-do: its source stays its own ("Calis AI"), and an "In Fellow" chip follows it.
 */
export function isTiedToFellow(todo: Pick<TodoDto, 'source' | 'fellow'>): boolean {
  return todo.source !== 'fellow' && (todo.fellow?.items.some((item) => item.matchedBy !== null) ?? false);
}

/**
 * Whether a row wears the Fellow chip: on a `fellow` row in place of its muted "Fellow" source words; on a row Calis tied a
 * Fellow item to, after its own source words, always — a tie has something to say whatever the switch: that the item is
 * also in Fellow, and "Not the same…". Why a `fellow` row has none while the switch is off with nothing to tell: a
 * deployment may switch the feature off (on by default since 2026-10-01), and a chip on every Fellow row that only said so would be noise;
 * a row that has something to say — a write paused by the switch, a failure, an offer, what Calis last wrote — keeps it.
 */
export function showsFellowChip(todo: Pick<TodoDto, 'source' | 'fellow'>, gate: FellowTickOffGate): boolean {
  const fellow = todo.fellow;
  if (!fellow) return false;
  if (todo.source !== 'fellow') return isTiedToFellow(todo);
  if (gate !== 'off') return true;
  return fellow.pending !== null || fellow.problem !== null || fellow.offer !== null || fellow.lastWritten !== null || fellow.reported !== null;
}

/** The chip's visible word: "Fellow" where it stands for a Fellow to-do's source, "In Fellow" after a tied to-do's own. */
export function fellowChipLabel(todo: Pick<TodoDto, 'source' | 'fellow'>): string {
  return isTiedToFellow(todo) ? 'In Fellow' : 'Fellow';
}

/** The chip's state in a few words: its tooltip, and — after "From Fellow: " — its accessible name. */
export function fellowChipState(todo: Pick<TodoDto, 'status' | 'fellow'>, gate: FellowTickOffGate): string {
  const fellow = todo.fellow;
  if (!fellow) return 'From Fellow';
  if (fellow.problem?.kind === 'failure') return todo.status === 'done' ? "Couldn't tick it off in Fellow" : "Couldn't update it in Fellow";
  if (fellow.pending !== null) return wantWords(todo.status).waiting;
  // Why before the gate: what came back from Fellow is said whatever ticking off there is (Stage 3 is its own switch).
  const reported = fellowReported(todo);
  if (reported) return reported.to === 'done' ? 'Completed in Fellow' : 'Reopened in Fellow';
  const here = fellowHereOffer(todo);
  if (here && fellowKeptApart(todo)) return here === 'reopen_here' ? 'Ticked off from an item kept apart' : 'Reopened from an item kept apart';
  if (here) return here === 'tick_here' ? 'Completed in Fellow' : 'Open in Fellow';
  if (gate === 'off') return 'Ticking off in Fellow is off';
  if (gate === 'not_allowed') return "Ticking off in Fellow isn't available to you";
  if (fellow.offer === 'tick_fellow') return 'Still open in Fellow';
  if (fellow.offer === 'reopen_fellow') return 'Still completed in Fellow';
  if (fellow.items.length > 0 && fellow.items.every((item) => item.state === 'done')) return 'Completed in Fellow';
  // Why a done to-do never says "Ticks off in Fellow too": its tick is made, and nothing is on its way (a write on its
  // way is said above), so the words must say what happens instead (the owner's ruling B3): Calis is checking the item
  // in Fellow, and offers the click if it is still open there (a tie on a to-do ticked off before Calis could tick off
  // in Fellow for the reader); Fellow is known to have it open; or Calis never ticked it off there and is not going to
  // by itself (a tick from before then, D14).
  if (todo.status === 'done' && fellow.items.some((item) => item.writable)) {
    if (fellow.checking) return FELLOW_CHECKING_TEXT;
    if (fellow.items.some((item) => item.writable && item.state === 'open')) return 'Still open in Fellow';
    if (fellow.items.some((item) => item.writable && item.state === null)) return FELLOW_NOT_TICKED_TEXT;
    return 'Completed in Fellow';
  }
  if (fellow.items.some((item) => item.writable)) return 'Ticks off in Fellow too';
  return "Calis can't tick this off in Fellow";
}

/** A done to-do whose item Calis is about to read in Fellow, to offer "Tick off in Fellow" if it is still open there. */
export const FELLOW_CHECKING_TEXT = 'Checking whether it’s still open in Fellow';

/**
 * A done to-do whose item Calis may write but owes nothing — it was ticked off before Calis could tick off in Fellow for
 * the reader (D14) — and whose state there Calis does not know: what happens next is nothing, and the words say so.
 */
export const FELLOW_NOT_TICKED_TEXT = 'Calis won’t tick this off in Fellow';

/** The chip's accessible name: "From Fellow: Ticks off in Fellow too" on a Fellow to-do, "In Fellow: …" on a tied one. */
export function fellowChipName(todo: Pick<TodoDto, 'source' | 'status' | 'fellow'>, gate: FellowTickOffGate): string {
  return `${isTiedToFellow(todo) ? 'In Fellow' : 'From Fellow'}: ${fellowChipState(todo, gate)}`;
}

export interface FellowItemPresentation {
  /** Fellow's wording on the reader's own copy, or null when Calis has none for it. */
  text: string | null;
  /** "Open in Fellow", "Completed in Fellow · 28 Sep 2026", "Archived in Fellow", …, or "Not known yet". */
  state: string;
  /** Why Calis will not write this item, when it will not and its state does not already say so. */
  note: string | null;
  /** How Calis tied the item to this to-do ("Same wording", "Matched by Calis AI"); null for a Fellow to-do's own item. */
  match: string | null;
}

/** How a tie was made, as the chip's popover says it (design §9.6; owner decision O2(b): an AI tie needs no confirmation). */
export const FELLOW_MATCH_LABELS: Readonly<Record<FellowItemMatchedBy, string>> = {
  wording: 'Same wording',
  ai: 'Matched by Calis AI',
};

/** One held item as the chip's popover shows it (design §9.6: Fellow's wording, then Fellow's state). */
export function fellowItemPresentation(item: TodoFellowDto['items'][number], problem: TodoFellowDto['problem'], timeZone?: string): FellowItemPresentation {
  const state = item.state === null
    ? 'Not known yet'
    : item.state === 'done' && item.stateAt
      ? `${FELLOW_ITEM_STATE_LABELS.done} · ${formatDate(item.stateAt, timeZone ? { timeZone } : undefined)}`
      : FELLOW_ITEM_STATE_LABELS[item.state];
  // Why the state alone for archived and "everyone completes": the label already says why nothing is written. An item
  // Calis may not write for any other reason either carries the not-possible reason the last write recorded, or —
  // with none recorded — is one Fellow gave no id (the four states' empty: design §9.6).
  const note = item.writable || item.state === 'archived' || item.state === 'per_person'
    ? null
    : problem?.kind === 'not_possible' && problem.code !== 'archived' && problem.code !== 'per_person'
      ? FELLOW_WRITE_CODE_LABELS[problem.code]
      : FELLOW_WRITE_CODE_LABELS.no_target;
  return { text: item.text, state, note, match: item.matchedBy === null ? null : FELLOW_MATCH_LABELS[item.matchedBy] };
}

/**
 * Whether the reader may say a tied item is "Not the same…" here (selection only: the route asks again): `on`; `off`
 * while Fellow is off on this server (FELLOW_MODE, the floor `fellow_connect` rides, which binds the superuser too); or
 * `not_allowed` — the reader's account does not include Connect Fellow. Why not the tick-off gate: the split rides
 * `fellow_connect` alone, whatever FELLOW_WRITE_MODE says, so a wrong tie can be undone while ticking off is off.
 */
export type FellowUntieGate = 'on' | 'off' | 'not_allowed';

export function fellowUntieGate(me: { integrations: { fellow: string }; capabilities: { fellowConnect: boolean } }): FellowUntieGate {
  if (me.integrations.fellow === 'off') return 'off';
  return me.capabilities.fellowConnect ? 'on' : 'not_allowed';
}

/** Why the popover offers no "Not the same…" on a tied to-do: said instead of a control that could not work. */
export const FELLOW_UNTIE_GATE_TEXT: Readonly<Record<Exclude<FellowUntieGate, 'on'>, string>> = {
  off: "Fellow is off on this server, so Fellow's item can't be separated from this to-do now.",
  not_allowed: "Separating Fellow's item from this to-do isn't available to you. An administrator can give you 'Connect Fellow'.",
};

/** The tied items of a to-do — each one the reader may say is "Not the same…". */
export function tiedFellowItems(fellow: Pick<TodoFellowDto, 'items'>): Array<TodoFellowDto['items'][number]> {
  return fellow.items.filter((item) => item.matchedBy !== null);
}

/**
 * "Not the same…" as the menu lists it: plain when the to-do holds one tied item, else naming the item by Fellow's words
 * (cut short), so each entry is legible out of context (03-components §4.2).
 */
export function fellowUntieMenuLabel(item: Pick<TodoFellowDto['items'][number], 'text'>, tiedCount: number): string {
  if (tiedCount <= 1 || item.text === null) return 'Not the same…';
  return `Not the same: “${truncate(item.text, 48)}”…`;
}

export interface FellowUntieDialogText {
  title: string;
  /** What happens, and what it means in Fellow. */
  body: string;
  /** That it is for good: Calis never ties them again. */
  lasting: string;
  confirmLabel: string;
}

/**
 * The words of the "Not the same…" confirmation (design §5.5): the title states the consequence, the body says what
 * happens here and in Fellow — Calis reopens the item there only when its own write had closed it, and only while it may
 * tick off in Fellow for the reader (`gate`; otherwise the reader is told to reopen it there themselves) — and that it
 * lasts; the confirm button carries the verb. Why a confirmation and not an undo: the split is for good (never matched
 * again).
 *
 * `todo`, the to-do the item is tied to: when its status came back from Fellow through this item ("Completed in
 * Fellow", "Reopened in Fellow" — Stage 3), the body says that the to-do keeps it. Why (owner ruling C5, "Offer to
 * reopen": "Leave the to-do as it is and offer a one-click 'Reopen here'; nothing reaches colleagues or Fellow unless
 * they click"): the tie the person is undoing is what brought that status here, and the row's line naming it goes with
 * the item — so the person is told before, never after, that the to-do stays ticked off and that its row then offers
 * Reopen here. A to-do another item it holds still shows done in Fellow stands on that one (no offer, and nothing said).
 * The mirror (owner reading "C5 mirror", "Yes, offer it": "Same as C5 in reverse: leave it open, offer 'Tick off here';
 * clicking is their own tick (reaches colleagues and Fellow)"): a to-do a reopen in Fellow reopened through this item
 * stays open, and its row then offers Tick off here — the person's own tick, which the body says reaches what any tick
 * of theirs reaches from the to-do as it is left (`fellowKeptOpenTick`: its other Fellow items, `link`) — on the server's
 * own condition: its open is still Fellow's (`statusFromFellow`), the item open in Fellow, no other item it holds open
 * there. Why a done is read from the to-do's own completion, not from the
 * row's "Completed in Fellow" (round C review): the server keeps the to-do apart on exactly that — a done Fellow's change
 * set (`completedVia`), the item done in Fellow, no other item it holds done there (fellow/import-or-tie.ts
 * `untieFellowItem`) — and a to-do holding several items keeps a done Fellow set after the item that reported it turned
 * back in Fellow, when the row no longer says "Completed in Fellow". `stateShown`: whether the reader is shown what comes
 * back from Fellow (`me.integrations.fellowState`, with Connect Fellow) — the row's Reopen here is shown only then, so
 * it is promised only then.
 */
export function fellowUntieDialog(
  item: Pick<TodoFellowDto['items'][number], 'closedByCalis'> & Partial<Pick<TodoFellowDto['items'][number], 'ref' | 'state'>>,
  gate: FellowTickOffGate,
  todo?: {
    status: TodoDto['status'];
    completedVia?: TodoDto['completedVia'];
    stateShown?: boolean;
    link?: TodoDto['link'];
    fellow: (Pick<TodoFellowDto, 'items' | 'reported'> & Partial<Pick<TodoFellowDto, 'statusFromFellow'>>) | null;
  },
): FellowUntieDialogText {
  const fellow = !item.closedByCalis
    ? 'Nothing changes in Fellow.'
    : gate === 'on'
      ? 'Calis had ticked it off in Fellow; it will reopen it there.'
      : `Calis had ticked it off in Fellow and can't reopen it there now. ${FELLOW_GATE_TEXT[gate]} Reopen it in Fellow yourself if it is still to do.`;
  const reported = todo?.fellow?.reported?.to === todo?.status ? (todo?.fellow?.reported?.to ?? null) : null;
  const others = (todo?.fellow?.items ?? []).filter((other) => other.ref !== item.ref);
  const doneFromFellow = todo?.stateShown === true && todo.status === 'done' && todo.completedVia === 'fellow';
  const openFromFellow = todo?.stateShown === true && todo.status === 'open' && todo.fellow?.statusFromFellow === true;
  const keeps =
    doneFromFellow && item.state === 'done' && !others.some((other) => other.state === 'done')
      ? ' This to-do was ticked off because that item was ticked off in Fellow; it stays ticked off, and its row offers Reopen here if the work is still to do.'
      : openFromFellow && item.state === 'open' && !others.some((other) => other.state === 'open')
        ? ` This to-do was reopened because that item was reopened in Fellow; it stays open, and its row offers Tick off here if the work is done. ${fellowKeptOpenTick({ fellowItems: others.length > 0, linked: (todo?.link ?? null) !== null })}`
        : reported === 'open' && todo?.fellow?.items.length === 1
          ? ' This to-do was reopened because that item was reopened in Fellow; it stays open.'
          : '';
  return {
    title: 'Keep Fellow’s item as its own to-do?',
    body: `Calis will keep this to-do and add Fellow's item to your list as its own to-do. ${fellow}${keeps}`,
    lasting: 'Calis won’t tie the two again.',
    confirmLabel: 'Keep them separate',
  };
}

/**
 * What the Regenerate dialog adds about Fellow's items (design §9.6), or null: said only where Calis ties them here
 * (FELLOW_MATCH_MODE, as /api/me reports it) and the reader connects Fellow — elsewhere a new summary leaves Fellow's
 * items as Stage 1 did, and the sentence would promise what does not happen. Why the dismissal is said too: a new
 * summary may write an AI to-do of the same work as a Fellow to-do the reader has not touched, which Calis's look then
 * dismisses in its favour (owner decision O8(c)) — a change to their list the confirmation must not leave out; "may",
 * because only a look certain of the pair does it.
 */
export function regenerateFellowLine(me: { integrations: { fellowMatch: 'on' | 'off' }; capabilities: { fellowConnect: boolean } }): string | null {
  if (me.integrations.fellowMatch !== 'on' || !me.capabilities.fellowConnect) return null;
  return "To-dos that are also in Fellow stay tied when the new summary has them. Fellow's other items are added as Fellow to-dos, and a Fellow to-do you haven't touched may be dismissed in favour of a new AI to-do of the same work (you can restore it).";
}

/** The toast after "Not the same…": it says a reopen in Fellow only when the server says one is owed. */
export function fellowUntieAnnouncement(reopening: boolean): string {
  return reopening ? 'Kept apart. Reopening it in Fellow.' : 'Kept apart.';
}

/** What a tick here does in Fellow, as the popover says it once the reader may tick off there. */
export const FELLOW_TICK_OFF_EXPLANATION =
  'When you tick it off here, Calis ticks it off in Fellow too, with your own Fellow key. Reopening it here reopens it in Fellow only if Calis ticked it off there.';

/**
 * What a colleague's tick does in Fellow, said on a linked Fellow to-do (owner decision O3(b), "Yes, every time"): Fellow
 * then shows the reader completing an item they never ticked themselves.
 */
export const FELLOW_LINKED_EXPLANATION = "A colleague's tick that reaches it through its link ticks it off in Fellow too, with your key.";

/**
 * What the chip's popover says a tick here does in Fellow, once the reader may tick off there: nothing unless some held
 * item is one Calis may write. Why: on an item assigned to others, archived, each person's own part or without an id,
 * "Calis ticks it off in Fellow too" would contradict the item's own line just above it.
 */
export function fellowTickOffExplanations(fellow: Pick<TodoFellowDto, 'items'>, linked: boolean): string[] {
  if (!fellow.items.some((item) => item.writable)) return [];
  return linked ? [FELLOW_TICK_OFF_EXPLANATION, FELLOW_LINKED_EXPLANATION] : [FELLOW_TICK_OFF_EXPLANATION];
}

/** What Calis last wrote to Fellow for this to-do: "Calis ticked it off in Fellow · 28 Sep 2026". */
export function fellowLastWrittenLine(fellow: Pick<TodoFellowDto, 'lastWritten'>, timeZone?: string): string | null {
  if (!fellow.lastWritten) return null;
  const date = formatDate(fellow.lastWritten.at, timeZone ? { timeZone } : undefined);
  return `${fellow.lastWritten.to === 'done' ? 'Calis ticked it off in Fellow' : 'Calis reopened it in Fellow'} · ${date}`;
}

// --- A new to-do ---------------------------------------------------------------------------------

/**
 * What the "Add a to-do" form can open with instead of blank: a suggestion the person reviews, edits
 * and adds as their own — an opportunity's suggested to-do from a Slack check (owner ruling D4). The
 * to-do it becomes is `manual`, like any other the person writes (ADR 0007).
 */
export interface TodoDraft {
  text: string;
  priority?: TodoPriority;
}

/** The form's first values: the draft's, or a blank medium-priority to-do with no due date. */
export function newTodoValues(draft: TodoDraft | undefined): { text: string; dueDate: string; priority: TodoPriority } {
  return { text: draft?.text ?? '', dueDate: '', priority: draft?.priority ?? 'medium' };
}

/**
 * Whether the person has changed the form since it opened — what closing it would lose. A draft they
 * have not touched is not their work, so closing it asks nothing.
 */
export function todoDraftChanged(values: { text: string; dueDate: string; priority: TodoPriority }, draft: TodoDraft | undefined): boolean {
  const start = newTodoValues(draft);
  return values.text.trim() !== start.text.trim() || values.dueDate !== start.dueDate || values.priority !== start.priority;
}

/** Where a to-do sits on its meeting's page: the to-dos tab, the row brought into view and identified. */
export function meetingTodoSearch(todoId: string): string {
  return `?tab=todos&todo=${encodeURIComponent(todoId)}`;
}
